// Config-driven registry for self-contained, gated sport areas (football, basketball, ...).
// Each entry parametrizes the generic Sport* portal components, routes, nav gating,
// signup/login redirects and wizard pre-configuration. Adding a new gated sport is
// (mostly) a matter of adding an entry here + a thin route folder under routes/_app/<key>/.

import {ParticipantsType, TEAM_SIDES} from '../constants.ts';
import {
    currentRegistry,
    singleScoreForSport as registrySingleScoreForSport,
    SportDefinition,
    sportDefinition,
    sportSupportsDraw as registrySportSupportsDraw,
} from './registry.ts';

export interface SportAreaConfig {
    /** SportType key — must match backend SportType + frontend SportScoringType/SPORT_STATS keys. */
    key: string;
    /** Display name, e.g. "Football". */
    label: string;
    /** Iconify icon id. */
    icon: string;
    /** Route base path with leading slash and no trailing slash, e.g. "/football". */
    basePath: string;
    /**
     * Where an area user lands after login/signup and when bounced back into their area.
     * Defaults to `${basePath}/leagues/` — the team-sport areas all open on their league list.
     * Running has no leagues, so it points at its own dashboard instead.
     */
    homePath?: string;
    /**
     * Races rather than leagues/tournaments (running). The generic Sport* portal components and
     * team-sport nav don't apply, so the area supplies its own routes under basePath.
     */
    isRaceArea?: boolean;
    /** Short tagline shown on the portal hero. */
    tagline: string;
    /** Noun for a unit of scoring, e.g. "Goals" (football) / "Points" (basketball). Used in dashboard labels. */
    scoreNoun: string;
    /** Playing positions offered in the roster editor. */
    positions: string[];
    /**
     * League standings style:
     *  - 'goals'  → W/D/L, goals for/against, GD, points (football-style)
     *  - 'record' → GP, W, L, Win%, +/- point differential (basketball-style)
     */
    standingsStyle: 'goals' | 'record';
    /** Participants type for leagues/tournaments created in this area (teams for these sports). */
    participantsType: number;
    /** League points config. */
    pointsPerWin: number;
    pointsPerDraw: number;
    /** Team-based sport areas skip the player-detail (elo/grade) signup step. */
    skipPlayerDetails: boolean;
    /**
     * Optional period-by-period score entry (e.g. basketball quarters). When set, the score
     * input offers an "enter by period" toggle; entered periods are SUMMED into the single
     * final score that is stored (winner/standings stay total-points based).
     */
    periods?: {count: number; label: string};
    /** Pre-select the league type in the create wizard (e.g. 'points' for volleyball 3-2-1-0). */
    leagueType?: 'win_percent' | 'points' | 'ladder' | 'season';
    /** Default best-of for set-based team sports (volleyball) — stored as the event's sets count. */
    defaultBestOf?: number;
}

/**
 * The hand-kept, UI-only part of an area config. Everything the sport registry knows — label, icon,
 * score noun, league points — is filled in from the registry by `buildArea`, so this
 * holds only what the registry has no say in: paths, taglines, positions, standings style, periods,
 * league type, best-of and the signup shape. `scoreNoun`/`pointsPerWin`/`pointsPerDraw` may be
 * given here to override the registry for an area whose config is not derivable from it (running).
 */
type SportAreaUiConfig = Omit<SportAreaConfig, 'label' | 'icon' | 'scoreNoun' | 'pointsPerWin' | 'pointsPerDraw'>
    & Partial<Pick<SportAreaConfig, 'scoreNoun' | 'pointsPerWin' | 'pointsPerDraw'>>;

const capitalise = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * What the dashboards count for an area, from the registry's terms: the score noun for a sport
 * decided on one final score (Goals, Points, Runs); the unit, pluralised, for a sport decided on
 * sets, games or maps (Sets, Games, Maps) — its score_noun is the points inside a set, which is
 * not what the dashboard totals.
 */
function areaScoreNoun(definition: SportDefinition): string {
    if (definition.scoring_type === 'score') return capitalise(definition.terms.score_noun);
    return capitalise(definition.terms.unit) + 's';
}

function buildArea(ui: SportAreaUiConfig): SportAreaConfig {
    const definition = sportDefinition(ui.key);
    return {
        ...ui,
        label:         definition?.label ?? ui.key,
        icon:          definition?.icon ?? 'material-symbols-light:sports',
        scoreNoun:     ui.scoreNoun ?? (definition ? areaScoreNoun(definition) : 'Points'),
        pointsPerWin:  ui.pointsPerWin ?? definition?.standings.win ?? 0,
        pointsPerDraw: ui.pointsPerDraw ?? definition?.standings.draw ?? 0,
    };
}

const SPORT_AREA_UI: SportAreaUiConfig[] = [
    {
        key:               'football',
        basePath:          '/football',
        tagline:           'Manage your team leagues & tournaments',
        positions:         ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'],
        standingsStyle:    'goals',
        participantsType:  ParticipantsType.DoublesTeam,
        skipPlayerDetails: true,
    },
    {
        key:               'basketball',
        basePath:          '/basketball',
        tagline:           'Manage your team leagues & tournaments',
        positions:         ['Guard', 'Forward', 'Center'],
        standingsStyle:    'record',
        participantsType:  ParticipantsType.DoublesTeam,
        skipPlayerDetails: true,
        periods:           {count: 4, label: 'Q'},
    },
    {
        key:               'esports',
        basePath:          '/esports',
        tagline:           'Manage your team & 1v1 esports leagues & tournaments',
        positions:         ['Starter', 'Substitute', 'Coach'],
        standingsStyle:    'record',
        // Default to teams; esports is 'series' scoring so the wizard's participant-type
        // selector is shown, letting the organizer pick Teams or 1v1 per competition.
        participantsType:  ParticipantsType.DoublesTeam,
        skipPlayerDetails: true,
    },
    {
        key:               'cricket',
        basePath:          '/cricket',
        tagline:           'Manage your team leagues & tournaments',
        positions:         ['Batter', 'Bowler', 'All-rounder', 'Wicketkeeper'],
        standingsStyle:    'goals',
        participantsType:  ParticipantsType.DoublesTeam,
        skipPlayerDetails: true,
    },
    {
        key:               'volleyball',
        basePath:          '/volleyball',
        tagline:           'Manage your team leagues & tournaments',
        // Set-based sport: the VolleyballScorecard enters points per set; standings rank on match
        // points (3-2-1-0) with sets won/lost, so the 'goals' style (points + for/against) fits best.
        positions:         ['Setter', 'Outside Hitter', 'Middle Blocker', 'Opposite', 'Libero'],
        standingsStyle:    'goals',
        participantsType:  ParticipantsType.DoublesTeam,
        skipPlayerDetails: true,
        leagueType:        'points',   // official 3-2-1-0 table
        defaultBestOf:     5,          // best of 5 by default; organizer can set 3 per event
    },
    {
        key:               'roundnet',
        basePath:          '/roundnet',
        tagline:           'Manage your 2v2 leagues & tournaments',
        // Roundnet (spikeball) is played best-of-three games to 21 (win by 2). Like volleyball it is a
        // 'sets' sport — the winner is decided on games won — so the 'goals' standings style (points +
        // games for/against) fits, ranked on a simple 3-points-per-win table.
        positions:         ['Player'],
        standingsStyle:    'goals',
        participantsType:  ParticipantsType.DoublesTeam,
        skipPlayerDetails: true,
        leagueType:        'points',
        defaultBestOf:     3,          // best of 3 games by default; organizer can change per event
    },
    {
        key:               'running',
        basePath:          '/running',
        // Races, not leagues — the area supplies its own routes (dashboard, races, check-in,
        // results upload, season leaderboards) rather than the generic Sport* portal.
        isRaceArea:        true,
        homePath:          '/running/dashboard/',
        tagline:           'Manage your races, entries & season leaderboards',
        // A race has no score noun in the registry's sense (its unit is km, its score a time), and
        // the team-sport fields below don't drive anything for races; they're here because every
        // area config carries them. Season scoring lives on the leaderboard (points_per_place).
        scoreNoun:         'Points',
        positions:         ['Runner'],
        standingsStyle:    'record',
        participantsType:  ParticipantsType.Singles,
        skipPlayerDetails: true,
    },
];

/** The gated sport areas, keyed by sport. Label, icon, score noun and league points come from the registry. */
export const SPORT_AREAS: Record<string, SportAreaConfig> = Object.fromEntries(
    SPORT_AREA_UI.map((ui) => [ui.key, buildArea(ui)]),
);

/** All gated sport-area configs, in display order. */
export const SPORT_AREA_LIST: SportAreaConfig[] = Object.values(SPORT_AREAS);

/**
 * Where to send an area user — after login, after signup, and when they wander outside their area.
 * Team-sport areas open on their league list; running opens on its race dashboard.
 */
export function sportAreaHome(cfg: SportAreaConfig): string {
    return cfg.homePath ?? `${cfg.basePath}/leagues/`;
}

/**
 * The address prefix an event of this sport lives under: "/basketball" for an area sport, "" for
 * everything else (racquet sports, sports with no area of their own). Race areas have no
 * tournament or league pages, so they get "" too.
 *
 * Anything that sends an organizer to a tournament or league page by sport must build the address
 * with this. An account whose sport has an area is CONFINED to it (routes/_app.tsx) — a racquet
 * address only reaches the right page by way of that redirect, which costs a second page load.
 * The quiz used to prefix football alone.
 */
export function sportAreaBase(sport: string | null | undefined): string {
    const area = sport ? SPORT_AREAS[sport] : undefined;
    return area && !area.isRaceArea ? area.basePath : '';
}

/** League wizard initial state for a sport area (mirrors the old FOOTBALL_LEAGUE_INITIAL_STATE). */
export function leagueInitialState(cfg: SportAreaConfig) {
    return {
        sportType:        cfg.key,
        participantsType: cfg.participantsType,
        pointsPerWin:     cfg.pointsPerWin,
        pointsPerDraw:    cfg.pointsPerDraw,
        pointsPerSetType: 'none',
        pointsPerSet:     1,
        setsCount:        cfg.defaultBestOf ?? 1,
        ...(cfg.leagueType ? {leagueType: cfg.leagueType} : {}),
        returnPath:       cfg.basePath + '/',
    } as any;
}

/** Tournament wizard initial state for a sport area (mirrors the old FOOTBALL_TOURNAMENT_INITIAL_STATE). */
export function tournamentInitialState(cfg: SportAreaConfig) {
    return {
        sportType:        cfg.key,
        participantsType: cfg.participantsType,
        ...(cfg.defaultBestOf ? {setsCount: cfg.defaultBestOf} : {}),
        // Draw-capable sports (football, cricket) rank group stages by the points system
        // (3-1-0 for football) rather than raw match wins.
        ...(cfg.pointsPerDraw > 0 ? {rrRankingMethod: 'points'} : {}),
        returnPath:       cfg.basePath + '/',
    } as any;
}

/** The sport area whose basePath the given pathname falls under, if any. */
export function sportAreaForPath(pathname: string | undefined | null): SportAreaConfig | undefined {
    if (!pathname) return undefined;
    return SPORT_AREA_LIST.find((c) => pathname.startsWith(c.basePath + '/') || pathname === c.basePath);
}

/** The sport area matching a user's chosen sport (user.meta.sport), if it is a gated area. */
export function sportAreaForUser(user: any): SportAreaConfig | undefined {
    const sport = user?.meta?.sport;
    return sport ? SPORT_AREAS[sport] : undefined;
}

/** True if a wizard returnPath points into any gated sport area (was: returnPath.includes('/football')). */
export function isTeamSportAreaPath(returnPath: string | undefined | null): boolean {
    if (!returnPath) return false;
    return SPORT_AREA_LIST.some((c) => returnPath.includes(c.basePath));
}

/**
 * Single-score entry (one box per team) vs multi-set/map entry, for the sport area at a path.
 *  - true  → 'score' sport (football, basketball): single final score
 *  - false → 'sets'/'series' sport area (e.g. esports maps): multi-entry
 *  - undefined → not in a sport area; caller falls back to the game's sets_count
 */
export function singleScoreForPath(pathname: string | undefined | null): boolean | undefined {
    const area = sportAreaForPath(pathname);
    if (!area) return undefined;
    return registrySingleScoreForSport(area.key);
}

/**
 * Single-score (one box per team) vs multi-set entry, by the event's sport. True only for 'score'
 * sports (football, basketball); racquet/sets sports (tennis, pickleball, padel…) and 'series'
 * sports use multi-set entry — so a 1-set racquet match can still have more sets added.
 */
export function singleScoreForSport(sport: string | undefined | null): boolean {
    return registrySingleScoreForSport(sport);
}

/**
 * The non-racquet competition areas: football, basketball, esports, cricket, volleyball, roundnet.
 * Running is excluded — it is a race area with individual entrants, not sides.
 *
 * Distinct from `isTeamBasedSport`, which asks whether the sport itself is played by named teams
 * whatever its area. Use this one for "does this area's setup form ask team-shaped questions"; use
 * that one for "do entrants compete as teams".
 */
export function isTeamSportArea(sport: string | undefined | null): boolean {
    const cfg = sport ? SPORT_AREAS[sport] : undefined;
    return !!cfg && !cfg.isRaceArea;
}

/**
 * Is this sport only ever played by named teams? Drives the entrant screens (add a team by name vs
 * the Player 1 / Player 2 / Seed table) and the team schedule tools. It reads the definition's
 * `sides` (Prompt 7): true when every side it allows is a named team (team, triples, fours).
 * Football, rugby, volleyball: yes. Esports (teams or 1v1), roundnet (2v2 pairs), darts, pétanque:
 * no, the event's own participants type decides (see entersAsTeams). For the 18 sports that exist
 * without the flag this is exactly the answer they always had.
 */
export function isTeamBasedSport(sport: string | undefined | null): boolean {
    const sides = sportDefinition(sport)?.sides || [];
    return sides.length > 0 && sides.every((side) => TEAM_SIDES.includes(side));
}

/**
 * Does THIS event enter named teams? A team sport always does; with multi-sport on, so does any
 * sport entering its team side (esports teams, pétanque triples, lawn bowls fours, darts teams) —
 * never a racquet sport, whose DoublesTeam is a fixed pair. Flag OFF: exactly isTeamBasedSport.
 */
export function entersAsTeams(sport: string | undefined | null, participantsType: number | string | null | undefined): boolean {
    if (isTeamBasedSport(sport)) return true;
    if (!currentRegistry().multi_sport || Number(participantsType) !== ParticipantsType.DoublesTeam) return false;
    const definition = sportDefinition(sport);
    return !!definition && !definition.racquet && definition.sides.some((side) => TEAM_SIDES.includes(side));
}

/**
 * Can a match in this sport end level? Mirrors the backend's SportType::supportsDraw (the
 * registry's supports_draw): the team sports decided on one final score (football, basketball,
 * hockey, rugby, cricket, baseball, american football). Set-based and series sports always
 * produce a winner.
 *
 * Use this, not the area's default pointsPerDraw, to decide whether an organizer may award draw
 * points or a table needs a Drawn column: that default is only what a draw is worth out of the box
 * (0 for basketball), which is a different question from whether draws can happen at all.
 */
export function sportSupportsDraw(sport: string | undefined | null): boolean {
    return registrySportSupportsDraw(sport);
}
